import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import type { CallbackInterface } from 'recoil';
import { useOnUnload } from 'hooks/useOnUnload';
import { useMutation, useApolloClient } from '@apollo/client';
import clone from 'ramda/es/clone';
import {
  steppedHPConfigurations,
  clinicalStepsBaseline,
  hangingProtocolStepIndex,
  isCreatingStepState,
  getStepsFromVersioned,
  makeHPKey,
  steppedHPInitSettledFor,
} from '../state';
import { stepBucketAt } from '../stepUtils';
import {
  addOptimisticStep,
  applyBaselineOrder,
  cancelAllPendingDeletions,
  clearAllPendingMutations,
  clearPendingCreates,
  clearPendingDeletions,
  clearPendingSaves,
  commitStepBaseline,
  confirmOptimisticStep,
  deletePendingCreate,
  deletePendingDeletion,
  deletePendingSave,
  discardPendingCreates,
  dropBaselineAfterDelete,
  dropBaselineForPendingDelete,
  getPendingCreate,
  getPendingDeletion,
  getPendingSave,
  noteSaveAwaitingCreate,
  pendingCreateEntries,
  pendingCreateSmids,
  pendingDeletionEntries,
  pendingSaveEntries,
  reinsertStepAt,
  resetStepIndices,
  restoreBaselineForUndoneDelete,
  revertAllToBaseline,
  revertStepsToBaseline,
  rollbackOptimisticStep,
  rollbackStepBaseline,
  rollbackStepBaselineAt,
  selectStep,
  selectStepWithIdentity,
  setPendingCreate,
  setPendingDeletion,
  setPendingSave,
  stageStepEdit,
  stageStepEdits,
  stageSteps,
  takeSaveAwaitingCreate,
} from '../clinicalStepStore';
import {
  beginPendingStepCreate,
  beginPendingStepDelete,
  clearPendingStepCreates,
  endPendingStepDelete,
  endPendingStepCreate,
  beginPendingStepSave,
  endPendingStepSave,
  isPendingStepWrite,
} from '../pendingStepMutations';
import { stepToGqlInput, toAllowedViewportType } from '../steppedHPTypes';
import type { StepConfiguration } from '../steppedHPTypes';
import { stepViewportConfigsToDehydrated, stepLayoutsToLayouts } from './useStepFormSync';
import { isStepEqual } from './useClinicalStepDirtyState';
import {
  UPDATE_HANGING_PROTOCOL_STEP,
  CREATE_HANGING_PROTOCOL_STEP,
  DELETE_HANGING_PROTOCOL_STEP,
  HANGING_PROTOCOL,
  GET_BASE_VIEWER_DATA,
} from 'modules/Apollo/queries';
import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import {
  deletePrecacheQuery,
  updatePrecacheQuery,
} from 'modules/Apollo/precache/precacheOperation';
import { logger } from 'modules/logger';
import {
  createDefaultStep,
  generateDefaultStepName,
  isClientGeneratedSmid,
  planStepOrderMoves,
  restoreStepOrder,
} from '../hpStepController';
import { createStepToastContentComponent, getDefaultHPToastOptions } from '../Steps/StepToast';
import { HP_TOAST_UNDO_DURATION } from '../Steps/hpStepToastMessages';
import {
  CLINICAL_REORDER_FAILED_TOAST,
  CLINICAL_SAVE_FAILED_TOAST,
  CLINICAL_STEP_CREATED_TOAST,
  CLINICAL_STEP_CREATED_WITH_REVERT_TOAST,
  CLINICAL_STEP_REVERTED_TOAST,
  CLINICAL_STEP_SAVED_TOAST,
  CREATE_FAILED_TOAST,
  DELETE_FAILED_TOAST,
} from '../Steps/stepsToastFactory';
import { composeHPFailureMessage } from './extractHPBackendErrorMessage';
import {
  viewportsConfigurationsState,
  activeHangingProtocolSmidState,
} from '../../ViewportsConfigurations/state';
import {
  clearStepViewportOverrides,
  publishStepHangingProtocol,
  renameStepBucket,
  restoreStepViewportOverrides,
} from '../../ViewportsConfigurations/viewportStepWriters';
import { FLAT_BUCKET, readBucket } from '../../ViewportsConfigurations/stepBuckets';
import type { StepBucketKey } from '../../ViewportsConfigurations/stepBuckets';
import type { DehydratedViewportsConfigurations } from '../../ViewportsConfigurations/types';
import { applyProtocolLayouts } from '../../Viewer/StudyLoader/viewerLayoutWriters';
import { layoutsForAllCasesState } from '../../Viewer/StudyLoader/viewerLoaderState';
import type { Layouts } from '../../Viewer/StudyLoader/viewerLoaderState';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';

/**
 * Shape of a precached getBaseViewerData response that contains a
 * matched hanging protocol with steps.
 */
interface CachedMatchedHP {
  smid?: string;
  steps?: Array<{ smid?: string } & Record<string, unknown>>;
}
interface CachedBaseViewerData {
  data?: {
    matchHangingProtocolByWorklistsAndStudies?: CachedMatchedHP | null;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Narrow an opaque precache transform payload (`updatePrecacheQuery` passes the parsed JSON as
 * `unknown`) down to the matched-HP node, validating each level at runtime rather than asserting
 * the whole shape. Returns `undefined` when the payload isn't the expected getBaseViewerData shape.
 */
function getMatchedHPFromCache(data: unknown): CachedMatchedHP | undefined {
  if (!isRecord(data) || !isRecord(data.data)) return undefined;
  const hp = data.data.matchHangingProtocolByWorklistsAndStudies;
  return isRecord(hp) ? (hp as CachedMatchedHP) : undefined;
}

/**
 * Append a step to the cached getBaseViewerData response for the given HP.
 * Returns `null` if the cache entry doesn't match the target HP or has no
 * steps array, signalling the caller to leave the entry unchanged.
 */
function appendStepToMatchedHP(
  data: unknown,
  hpSmid: string,
  step: Record<string, unknown>
): unknown | null {
  const typed = data as CachedBaseViewerData;
  const hp = getMatchedHPFromCache(data);
  if (!hp || hp.smid !== hpSmid || !Array.isArray(hp.steps)) return null;
  // Skip if the step is already present (e.g. duplicate write from another tab)
  if (hp.steps.some((s) => s?.smid === step.smid)) return null;
  return {
    ...typed,
    data: {
      ...typed.data,
      matchHangingProtocolByWorklistsAndStudies: {
        ...hp,
        steps: [...hp.steps, step],
      },
    },
  };
}

/**
 * Propagate a persisted step change into the two HP caches a page load reads from: the Apollo
 * `HangingProtocol` fragment and the `getBaseViewerData` precache entry.
 *
 * Both save paths need this. The deferred path always did it; the immediate path (renames, which
 * auto-save with no undo window) did not, so a rename reached the backend but the precached
 * base-viewer response kept the old `display_name` and the next load rehydrated the stale name
 * (VX-4748). Shared so the two paths cannot drift again.
 *
 * Best-effort by design: a cache miss or write failure must not fail an already-persisted save.
 */
async function syncPersistedStepIntoHPCaches(
  apolloClient: ReturnType<typeof useApolloClient>,
  hpSmid: Nullable<string>,
  stepSmid: Nullable<string>,
  gqlPayload: ReturnType<typeof stepToGqlInput>
): Promise<void> {
  if (hpSmid && stepSmid != null) {
    try {
      const cachedHP = apolloClient.readFragment({
        id: `HangingProtocol:${hpSmid}`,
        fragment: HANGING_PROTOCOL,
      });
      if (cachedHP?.steps) {
        const updatedSteps = cachedHP.steps.map((cachedStep: { smid: string }) =>
          cachedStep.smid === stepSmid ? { ...cachedStep, ...gqlPayload } : cachedStep
        );
        apolloClient.writeFragment({
          id: `HangingProtocol:${hpSmid}`,
          fragment: HANGING_PROTOCOL,
          data: { ...cachedHP, steps: updatedSteps },
        });
      }
    } catch {
      // Best-effort cache update.
    }
  }

  await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA });
}

/** Pre-revert ("dirty") step snapshot captured so UNDO can re-apply it. */
export interface DirtyStepSnapshot {
  stepIndex: number;
  currentCaseId: Nullable<string>;
  /** The step's own bucket -- its smid, or {@link FLAT_BUCKET} for an unstepped protocol. */
  bucket: StepBucketKey;
  modifiedStep: StepConfiguration | null;
  modifiedViewports: DehydratedViewportsConfigurations | null;
  modifiedLayouts: Layouts | null;
}

/**
 * Re-apply a step's pre-revert snapshot to the viewer atoms. Shared by the UNDO action of both
 * `revertStep` and `createStepWithRevert`: it restores the captured step config, viewport
 * overrides, and layouts that were cleared when the step reverted to baseline. Exported for unit
 * testing. No-ops for any snapshot field that wasn't captured (null).
 */
export function restoreDirtyStepSnapshot(
  set: CallbackInterface['set'],
  {
    stepIndex,
    currentCaseId,
    bucket,
    modifiedStep,
    modifiedViewports,
    modifiedLayouts,
  }: DirtyStepSnapshot
): void {
  if (modifiedStep) {
    stageStepEdit(set, { stepIndex, step: modifiedStep });
  }
  if (currentCaseId && modifiedViewports) {
    restoreStepViewportOverrides(set, {
      caseId: currentCaseId,
      bucket,
      overrides: modifiedViewports,
    });
  }
  if (currentCaseId && modifiedLayouts) {
    // The undone layout still reaches the screen without the user override: modifiedStep carries it
    // into the step atom above, and the protocol layer is re-derived per step index on navigation.
    applyProtocolLayouts(set, { caseId: currentCaseId, layouts: modifiedLayouts });
  }
}

/**
 * How many `useClinicalStepOperations` instances are mounted.
 *
 * The deferred-mutation queues are module-level, and this hook is mounted in **two** places (StepBar
 * and HangingProtocolWidget) -- so one component unmounting is not the same event as "the viewer is
 * gone". StepBar unmounts during ordinary use: `ResizableStepBar` returns null while the HP edit
 * sidebar is open, and the bar remounts around a step switch. Cancelling the queues from that unmount
 * therefore threw away saves and deletes belonging to the instance still on screen, inside the ~9s
 * undo window, so the mutation never reached the network and the dirty indicator never cleared.
 *
 * Refcounting keeps the teardown cancel that VX-4811's coverage asks for -- stale timers must not
 * outlive the viewer -- while making it fire only once nothing is left to own the queues.
 */
let mountedInstanceCount = 0;

export function useClinicalStepOperations() {
  const apolloClient = useApolloClient();
  const [updateStepMutation] = useMutation(UPDATE_HANGING_PROTOCOL_STEP, {
    context: { telemetry: { component: 'useClinicalStepOperations' } },
  });
  const [createStepMutation] = useMutation(CREATE_HANGING_PROTOCOL_STEP, {
    context: { telemetry: { component: 'useClinicalStepOperations' } },
  });
  const [deleteStepMutation] = useMutation(DELETE_HANGING_PROTOCOL_STEP, {
    context: { telemetry: { component: 'useClinicalStepOperations' } },
  });
  const { enqueueToast, closeToast } = useToasterDispatch();

  // Track the number of in-flight backend mutations so the UI can show a sync indicator.
  const [pendingOperations, setPendingOperations] = useState(0);
  // Track deferred saves waiting for the undo window to expire. Drives the
  // "pending" spinner before the actual mutation fires.
  const [deferredSaveCount, setDeferredSaveCount] = useState(0);
  const trackOperation = useCallback(async <T>(operation: Promise<T>): Promise<T> => {
    setPendingOperations((n) => n + 1);
    try {
      return await operation;
    } finally {
      setPendingOperations((n) => n - 1);
    }
  }, []);

  const { currentCaseId } = useCurrentCaseContext();

  // Stale timers from a previous HP must not fire against the new one, and stale timers must not
  // outlive the viewer. Two separate triggers, because they are two separate events: the HP changing
  // (effect below) and the last mounted instance going away (refcounted, see `mountedInstanceCount`).
  const hpSmid = useRecoilValue(activeHangingProtocolSmidState);

  useEffect(() => {
    mountedInstanceCount += 1;
    return () => {
      mountedInstanceCount -= 1;
      if (mountedInstanceCount === 0) clearAllPendingMutations();
    };
  }, []);

  // The protocol Reset All's index reset belongs to. Reset All stays on the *same* protocol and means
  // the reader to be at its first step, so it records ownership rather than dropping it -- otherwise
  // the next re-read would read the index as unowned and home it again. HP switch and unmount pass null
  // instead, from `useClearSteppedHPState` (VX-4810 slice 5).
  const activeHPKey = makeHPKey(currentCaseId, hpSmid);

  // Drop the pending-create guard when the active HP actually changes. A create still in flight
  // belongs to the HP the user just left, and `useHangingProtocol` arms `__lastInitializedHPKey`
  // before it calls `applyFetchedSteps` -- so a guard left set across the switch does not delay the
  // new HP's initialization, it cancels it, and nothing retries.
  //
  // Deliberately in the effect body behind a previous-value ref rather than in a cleanup:
  // `useClinicalStepOperations` is mounted in more than one place (StepBar and
  // HangingProtocolWidget) and this registry is module-level, so a cleanup would let either instance
  // unmounting mid-create clear the guard the other still needs -- reopening VX-4811. Running on
  // mount would do the same on a StepBar remount.
  // Seeded with the current smid rather than null on purpose, and named for what it holds rather
  // than for the usual previous/current pattern: there is nothing to clear on the first render, and
  // a null seed would make the mount itself look like a switch and drop a guard the other mounted
  // instance may still need.
  //
  // The deferred-mutation queues move with it, and for the same reason. They used to be cleared from
  // this effect's *cleanup*, so either mounted instance unmounting wiped every queued save and delete
  // -- including ones belonging to the instance still on screen. That is one defect presenting three
  // ways: `stepped-hp-edit-form-reopen` (no `updateHangingProtocolStep` in 45s),
  // `stepped-hp-priors-per-step` ("waiting for step 1 to save"), and
  // `stepped-hp-preview-discards-unsaved-changes` (dirty still set after the discard). It reproduced
  // against deployed dev, so it was never environmental.
  const lastClearedHpSmidRef = useRef(hpSmid);
  useEffect(() => {
    if (lastClearedHpSmidRef.current === hpSmid) return;
    lastClearedHpSmidRef.current = hpSmid;
    clearAllPendingMutations();
    clearPendingStepCreates();
  }, [hpSmid]);

  /**
   * Revert a single step to its baseline snapshot.
   *
   * In addition to restoring the step atom, this also clears user viewport
   * overrides and writes the baseline step's dehydrated viewport configs
   * directly to the dehydratedHangingProtocol atom so the viewport selector
   * reflects the revert immediately (the effect-based chain fires after
   * paint). This is the fallback path when useClinicalViewportEditing's
   * revertWithSnapshot is not used (e.g., StepBar's context menu revert).
   */
  const revertStep = useRecoilCallback(
    ({ set, snapshot: recoilSnapshot }) =>
      (stepIndex: number, options?: { silent?: boolean }) => {
        // Read fresh rather than the closure-captured `baseline` (from useRecoilState
        // at the top of the hook) — see createStepWithRevert for why that can be stale.
        const currentBaseline = recoilSnapshot.getLoadable(clinicalStepsBaseline).getValue();
        if (!currentBaseline?.[stepIndex]) return;

        // The step being reverted always files against its own smid, regardless of what is on
        // screen -- a revert from the context menu can target a step the reader is not looking at.
        const bucket: StepBucketKey = currentBaseline[stepIndex].smid ?? FLAT_BUCKET;

        // Capture dirty state before reverting so UNDO can restore it.
        const versioned = recoilSnapshot.getLoadable(steppedHPConfigurations).getValue();
        const modifiedStep = versioned.steps?.[stepIndex]
          ? clone(versioned.steps[stepIndex])
          : null;
        let modifiedViewports: DehydratedViewportsConfigurations | null = null;
        let modifiedLayouts: Layouts | null = null;
        if (currentCaseId) {
          const vpState = recoilSnapshot
            .getLoadable(viewportsConfigurationsState(currentCaseId))
            .getValue();
          const bucketOverrides = readBucket(vpState.byStep, bucket);
          modifiedViewports = bucketOverrides ? clone(bucketOverrides) : null;
          const allCaseLayouts = recoilSnapshot.getLoadable(layoutsForAllCasesState).getValue();
          modifiedLayouts = allCaseLayouts[currentCaseId]
            ? clone(allCaseLayouts[currentCaseId])
            : null;
        }

        revertStepsToBaseline(set, {
          indices: new Set([stepIndex]),
          baseline: currentBaseline,
        });

        // A revert keeps the same bucket key and changes its contents -- bucketing removed the
        // staleness step navigation used to hit, but not this one. The only other writer of
        // dehydratedHangingProtocol's content is useHangingProtocol's effect, which fires after
        // paint, so without this direct write the viewport selector would render the pre-revert
        // (dirty) series for one frame before the effect catches up.
        if (currentCaseId) {
          publishStepHangingProtocol(set, {
            caseId: currentCaseId,
            bucket,
            dehydrated: stepViewportConfigsToDehydrated(currentBaseline[stepIndex].viewports),
          });
        }

        // Restore layout from the baseline step, and clear the case-scoped override rather than
        // pinning it to this step's layout. The override outranks the step-derived layouts in
        // useLayouts' merge and nothing clears it on step navigation, so setting it here reset the
        // layout of every OTHER step to the one being reverted -- which is what QA re-reported on
        // VX-4768 through "Reset to Original State". Same shape as revertAllStepsToBaseline and
        // resetToHP, which already omit the case's key.
        const baselineLayouts = stepLayoutsToLayouts(currentBaseline[stepIndex].layouts);
        applyProtocolLayouts(set, { caseId: currentCaseId, layouts: baselineLayouts });

        // Clear user viewport overrides
        if (currentCaseId) {
          clearStepViewportOverrides(set, { caseId: currentCaseId, bucket });
        }

        // Show undo toast (suppressed when called from add-step handler
        // which shows its own contextual toast).
        if (options?.silent) return;
        enqueueToast(CLINICAL_STEP_REVERTED_TOAST.message, {
          ...getDefaultHPToastOptions({ autoHideDuration: HP_TOAST_UNDO_DURATION }),
          ContentComponent: createStepToastContentComponent({
            actionLabel: CLINICAL_STEP_REVERTED_TOAST.actionLabel,
            countdownDuration: HP_TOAST_UNDO_DURATION,
            onAction: () =>
              restoreDirtyStepSnapshot(set, {
                stepIndex,
                currentCaseId,
                bucket,
                modifiedStep,
                modifiedViewports,
                modifiedLayouts,
              }),
          }),
        });
      },
    [currentCaseId, enqueueToast]
  );

  /**
   * Revert ALL steps to their baseline state and cancel any pending
   * deferred saves. Used by "Reset All" to clear all dirty indicators.
   */
  const revertAllStepsToBaseline = useRecoilCallback(
    ({ set, snapshot: recoilSnapshot }) =>
      () => {
        // Read fresh rather than the closure-captured `baseline` (from useRecoilState
        // at the top of the hook) — see createStepWithRevert for why that can be stale.
        const baselineSnapshot = recoilSnapshot.getLoadable(clinicalStepsBaseline).getValue();
        if (!baselineSnapshot) return;

        // Cancel all pending deferred saves
        for (const [stepSmid, { timer }] of pendingSaveEntries()) {
          clearTimeout(timer);
          deletePendingSave(stepSmid);
        }
        setDeferredSaveCount(0);

        // Cancel any deferred create so it cannot fire after the refetch below and re-add a step this
        // reset was asked to discard. setDeferredSaveCount(0) above already zeroed the counter, so the
        // discard must not decrement it again.
        discardPendingCreates(set, pendingCreateSmids(), () => {});

        // Then drop every *unsaved* step, identified by its client-generated smid.
        //
        // Cancelling pendingCreate alone is not enough, because there are two create paths and only one
        // of them is deferred. `createStepWithRevert` (used when a step is dirty) schedules a timer and
        // registers in pendingCreate; `createStepOnBackend` (used when nothing is dirty, which is the
        // state VX-4811's repro reaches by resetting first) fires immediately and registers nowhere. Both
        // write an optimistic step carrying a `step-{uuid}` smid into the baseline, and this function
        // restores the steps atom *from* the baseline -- so the step came back regardless of the timer,
        // and the protocol then also gained the server's copy from the refetch. Two chips, one name.
        //
        // Filtering on the smid covers both paths and any future one, because it keys off what actually
        // makes a step unsaved rather than off which queue happened to know about it.
        const savedBaseline = baselineSnapshot.filter(
          (step) => step.smid != null && !isClientGeneratedSmid(step.smid)
        );

        // Then put back every step whose deletion is still inside its undo window, cancelling the
        // backend DELETE rather than firing it. Flushing those was VX-4815: the modal the user just
        // accepted promises a revert to the last saved version, and that version still contains a
        // step deleted two seconds ago, so committing the delete is the one outcome the dialog rules
        // out.
        //
        // Replayed in reverse order of deletion, which is the only order that reconstructs the saved
        // one. Each stashed index was captured against the list as it stood immediately *before* that
        // step was removed, so undoing the most recent deletion first restores exactly the precondition
        // the next one's index was measured against. Sorting by index instead -- ascending or
        // descending -- treats every index as if it were measured against the same list, and both get
        // it wrong: deleting B then D from [A,B,C,D] stashes D at 2 (its position in [A,C,D], not in
        // the original), so ascending yields [A,B,D,C]; and two deletions can stash the *same* index
        // (delete B then C, both at 1), which descending order then permutes to [A,C,B,D].
        const cancelledDeletions = cancelAllPendingDeletions().reverse();
        for (const { smid } of cancelledDeletions) endPendingStepDelete(smid);
        const currentBaseline = cancelledDeletions.reduce<StepConfiguration[]>(
          (acc, entry) => reinsertStepAt(acc, entry) ?? acc,
          savedBaseline
        );

        if (savedBaseline.length !== baselineSnapshot.length || cancelledDeletions.length > 0) {
          // An authoritative write of the array in hand, not a prune: this array is the reset's
          // answer for both atoms, and a prune can only remove -- it cannot bring back a step whose
          // deletion was just cancelled.
          applyBaselineOrder(set, { baseline: currentBaseline });
        }

        // Restore step atom to the baseline in hand, not the atom's -- the prune above has not
        // committed yet from this callback's point of view.
        revertAllToBaseline(set, { baseline: currentBaseline });

        // Reset to step 0 and restore its viewport state. The sub-step index has to come along: it is
        // per-step, so leaving it where it was points it at sub-steps step 0 may not have. Same
        // reasoning as the clamp in useHangingProtocol, which resets both for the same reason
        // (VX-4783).
        resetStepIndices(set, { forHPKey: activeHPKey });
        const activeBaseline = currentBaseline[0];
        if (activeBaseline) {
          // Reset All always lands on step 0, so the bucket it publishes to is that step's own smid.
          const bucket: StepBucketKey = activeBaseline.smid ?? FLAT_BUCKET;
          // Same reasoning as the write-ahead in revertStep: step 0's bucket key doesn't change here,
          // only its contents do, and dehydratedHangingProtocol's only other writer is an
          // after-paint effect.
          if (currentCaseId) {
            publishStepHangingProtocol(set, {
              caseId: currentCaseId,
              bucket,
              dehydrated: stepViewportConfigsToDehydrated(activeBaseline.viewports),
            });
          }
          const baselineLayouts = stepLayoutsToLayouts(activeBaseline.layouts);
          applyProtocolLayouts(set, { caseId: currentCaseId, layouts: baselineLayouts });
          if (currentCaseId) {
            clearStepViewportOverrides(set, { caseId: currentCaseId, bucket });
          }
        }
      },
    [currentCaseId, activeHPKey]
  );

  /**
   * Pending backend saves, keyed by step smid.
   * The timeout fires the actual PATCH after the undo window expires.
   * Module-level so it survives StepBar remounts.
   */

  /** Fire the actual backend PATCH and update the Apollo cache. */
  const executeSaveOnBackend = useRecoilCallback(
    ({ snapshot, set }) =>
      async (
        stepSmid: string,
        gqlPayload: ReturnType<typeof stepToGqlInput>,
        rollbackStep: StepConfiguration | null
      ) => {
        deletePendingSave(stepSmid);
        try {
          await trackOperation(
            updateStepMutation({
              variables: { stepId: stepSmid, payload: gqlPayload },
            })
          );

          // Update the Apollo cache so resetToHP reads current data.
          const hpSmid = await snapshot.getPromise(activeHangingProtocolSmidState);
          await syncPersistedStepIntoHPCaches(apolloClient, hpSmid, stepSmid, gqlPayload);
        } catch (error) {
          // The optimistic save already advanced the baseline. Roll it back to
          // the pre-save snapshot so the step reads dirty again and the user can
          // retry, rather than silently losing the unsaved-changes indicator.
          if (rollbackStep) {
            rollbackStepBaseline(set, { smid: stepSmid, rollbackStep });
          }
          enqueueToast(
            composeHPFailureMessage({
              action: 'save step',
              fallback: CLINICAL_SAVE_FAILED_TOAST.message,
              error,
            }),
            {
              ...getDefaultHPToastOptions(),
              ContentComponent: createStepToastContentComponent({
                icon: CLINICAL_SAVE_FAILED_TOAST.icon,
              }),
            }
          );
        } finally {
          // Whether the PATCH landed, failed, or the cache sync threw, the server is no longer
          // mid-update for this step, so a fetched protocol can be trusted again.
          endPendingStepSave(stepSmid);
        }
      },
    [updateStepMutation, trackOperation, apolloClient, enqueueToast]
  );

  /** Schedule a backend PATCH after the undo toast window. */
  const scheduleSave = useCallback(
    (
      stepSmid: string,
      gqlPayload: ReturnType<typeof stepToGqlInput>,
      rollbackStep: StepConfiguration | null
    ) => {
      const existing = getPendingSave(stepSmid);
      if (existing) {
        // Rescheduling an existing pending save for this step — its timer's
        // decrement is cancelled below, so don't increment the counter again.
        clearTimeout(existing.timer);
      } else {
        setDeferredSaveCount((n) => n + 1);
      }

      // From here the step on screen is ahead of the server, and stays ahead until the PATCH lands.
      // Anything re-initializing steps from a fetched protocol in that window reverts the edit.
      beginPendingStepSave(stepSmid);

      const timer = setTimeout(() => {
        setDeferredSaveCount((n) => Math.max(0, n - 1));
        executeSaveOnBackend(stepSmid, gqlPayload, rollbackStep);
      }, HP_TOAST_UNDO_DURATION + 2_000);
      setPendingSave(stepSmid, { timer, payload: gqlPayload, rollbackStep });
    },
    [executeSaveOnBackend]
  );

  /** Cancel a pending backend save (called on undo). */
  const cancelPendingSave = useCallback((stepSmid: string) => {
    const pending = getPendingSave(stepSmid);
    if (pending) {
      clearTimeout(pending.timer);
      deletePendingSave(stepSmid);
      setDeferredSaveCount((n) => Math.max(0, n - 1));
      // UNDO puts the step back to its pre-save state, so the server is authoritative again.
      endPendingStepSave(stepSmid);
    }
  }, []);

  /** Immediately execute a single pending save (called on toast dismiss). */
  const flushPendingSaveForStep = useCallback(
    (stepSmid: string) => {
      const pending = getPendingSave(stepSmid);
      if (pending) {
        clearTimeout(pending.timer);
        setDeferredSaveCount((n) => Math.max(0, n - 1));
        executeSaveOnBackend(stepSmid, pending.payload, pending.rollbackStep);
      }
    },
    [executeSaveOnBackend]
  );

  /**
   * Immediately execute all pending backend saves, clearing their timers. Resolves once every
   * flushed mutation has settled, so a caller that reads the HP back from the server afterwards
   * can order that read behind the writes (VX-4740).
   */
  const flushPendingSaves = useCallback(async (): Promise<void> => {
    // Snapshot entries/count up front: executeSaveOnBackend deletes from
    // pendingSaves synchronously, so reading size afterward would be 0.
    const entries = pendingSaveEntries();
    const count = entries.length;
    const settled = entries.map(([stepSmid, { timer, payload, rollbackStep }]) => {
      clearTimeout(timer);
      return executeSaveOnBackend(stepSmid, payload, rollbackStep);
    });
    clearPendingSaves();
    if (count > 0) setDeferredSaveCount((n) => Math.max(0, n - count));
    await Promise.all(settled);
  }, [executeSaveOnBackend]);

  /**
   * Save a single step via the per-step PATCH mutation. Updates baseline on success.
   *
   * Accepts an optional `stepOverride` for cases where the caller has already
   * constructed the updated step (e.g. rename) and the Recoil atom hasn't
   * committed yet. When omitted, reads the step from the current atom snapshot.
   */
  const saveStep = useRecoilCallback(
    ({ snapshot, set }) =>
      async (stepIndex: number, stepOverride?: StepConfiguration) => {
        let step: StepConfiguration | undefined;

        if (stepOverride) {
          // Use baseline viewport/layout data so that a metadata-only save
          // (e.g. rename) doesn't accidentally commit pending viewport edits.
          // The override's non-viewport fields (display_name, config_details)
          // take precedence over the baseline. Read baseline from the snapshot
          // (not the closure) to get the committed value at call time.
          const currentBaseline = snapshot.getLoadable(clinicalStepsBaseline).getValue();
          const baselineStep = currentBaseline?.[stepIndex];
          step = baselineStep
            ? {
                ...baselineStep,
                display_name: stepOverride.display_name,
                config_details: stepOverride.config_details,
              }
            : stepOverride;
        } else {
          const versioned = await snapshot.getPromise(steppedHPConfigurations);
          const currentSteps = getStepsFromVersioned(versioned);
          step = currentSteps?.[stepIndex];
        }

        if (!step?.smid) return;

        // A step whose create has not resolved still carries the client-generated smid
        // `createDefaultStep` minted (e.g. "step-1742792077498"), which is not a backend UUID and
        // cannot be PATCHed. Record the intent so the create flushes it once the real smid lands,
        // rather than returning silently: a user who adds a step, edits it and saves within a second
        // or two lost the save with no mutation, no toast and nothing queued to retry, and the step
        // stayed dirty forever (VX-4810).
        //
        // Only when a create is actually outstanding. An orphaned client smid -- a create that failed
        // and rolled back -- has nothing left to flush it, so it keeps the original early return.
        //
        // Outstanding means either of two states, and asking only the first was a bug of its own.
        // `isPendingStepWrite` covers a create that has *fired*, but `createStepWithRevert` defers the
        // create behind the undo window and calls `beginPendingStepCreate` at fire time, ~9s later --
        // so for that whole window the step has a create coming that no registry knows about yet.
        // That is the common case, not the rare one: Add Step goes through the deferred path whenever
        // anything is dirty. `stepped-hp-priors-per-step` saves inside it on every iteration.
        if (isClientGeneratedSmid(step.smid)) {
          const hasCreateComing =
            isPendingStepWrite(step.smid) || getPendingCreate(step.smid) != null;
          if (hasCreateComing) noteSaveAwaitingCreate(step.smid);
          return;
        }

        // Merge the current viewer viewport overrides into the step so that
        // ALL viewports visible in the layout are captured — not just ones
        // explicitly written by writeViewportToStep. Without this, viewport
        // positions populated by the HP match (factory merge) at render time
        // but never clinically edited would be lost on save + reload.
        //
        // Overrides are filed against the step they were made on, so a save reads *that* step's
        // own bucket rather than whichever step is on screen -- a save of a non-active chip must
        // still fold in and clear its own pending overrides. The bucket resolves to null until
        // init settles; defer rather than fall back to the flat bucket, which could misattribute
        // pre-settle writes to the wrong step.
        let wasEnriched = false;
        const settledFor = await snapshot.getPromise(steppedHPInitSettledFor);
        const bucket: StepBucketKey | null =
          settledFor == null
            ? null
            : stepBucketAt(await snapshot.getPromise(steppedHPConfigurations), stepIndex);
        if (currentCaseId && !stepOverride && bucket != null) {
          const vpState = await snapshot.getPromise(viewportsConfigurationsState(currentCaseId));
          const overrides = readBucket(vpState.byStep, bucket);
          if (overrides) {
            const enrichedViewports = { ...step.viewports };
            for (const [viewportId, dehydrated] of Object.entries(overrides)) {
              if (dehydrated == null) continue;
              // Only add viewports that aren't already in the step data.
              // Existing step entries (from writeViewportToStep or the original
              // HP data) may have richer data like criteria.
              if (enrichedViewports[viewportId] != null) continue;
              wasEnriched = true;
              enrichedViewports[viewportId] = {
                display_name: dehydrated.seriesDescriptions?.[0] ?? '',
                study_smid: dehydrated.study?.smid ?? null,
                series_smid: dehydrated.series?.smid ?? null,
                view_type: toAllowedViewportType(dehydrated.viewType),
                series_criteria_source_text: dehydrated.seriesDescriptions?.[0] ?? null,
                comparison_index: dehydrated.comparisonIndex ?? null,
                stack_smid: dehydrated.stack?.smid ?? null,
                viewport_mirror_group_id: dehydrated.viewportMirrorGroupId ?? null,
              };
            }
            if (wasEnriched) {
              step = { ...step, viewports: enrichedViewports };
            }
          }
          // The step now reflects any case-scoped overrides that mattered (either already
          // present in step data, or just folded in above). Clear this step's bucket so it
          // doesn't resurface as an untracked edit on a later step navigation/reload (VX-4617).
          clearStepViewportOverrides(set, { caseId: currentCaseId, bucket });
        }

        const gqlPayload = stepToGqlInput(step);

        // Capture the pre-save baseline BEFORE advancing it. This is the safe
        // save point: the UNDO toast restores it, and a failed deferred save
        // rolls back to it (see executeSaveOnBackend) so the step stays dirty
        // and retryable.
        const preSaveBaseline = snapshot.getLoadable(clinicalStepsBaseline).getValue();
        const preSaveBaselineStep = preSaveBaseline?.[stepIndex]
          ? clone(preSaveBaseline[stepIndex])
          : null;

        // Advance the baseline immediately so the dirty indicator clears. Optimistic: the
        // pre-save entry captured above is what `executeSaveOnBackend` rolls back to on failure.
        commitStepBaseline(set, { stepIndex, step });

        // If the step was enriched with viewport overrides, write the enriched data back to the
        // step atom so it matches the baseline it was just committed against.
        if (wasEnriched) {
          stageStepEdit(set, { stepIndex, step: clone(step) });
        }

        if (stepOverride) {
          // Rename auto-saves fire immediately (no undo opportunity).
          try {
            await trackOperation(
              updateStepMutation({
                variables: { stepId: step.smid, payload: gqlPayload },
              })
            );

            // Same cache maintenance the deferred path does. Skipping it was VX-4748: the rename
            // reached the backend, but the precached getBaseViewerData response still held the old
            // display_name, and the next load rehydrated from it and showed the old step name.
            const renameHPSmid = await snapshot.getPromise(activeHangingProtocolSmidState);
            await syncPersistedStepIntoHPCaches(apolloClient, renameHPSmid, step.smid, gqlPayload);
          } catch (err) {
            enqueueToast(
              composeHPFailureMessage({
                action: 'save step',
                fallback: CLINICAL_SAVE_FAILED_TOAST.message,
                error: err,
              }),
              {
                ...getDefaultHPToastOptions(),
                ContentComponent: createStepToastContentComponent({
                  icon: CLINICAL_SAVE_FAILED_TOAST.icon,
                }),
              }
            );
            // Roll the baseline back to its pre-save snapshot so the step returns
            // to a dirty state the user can retry. Without this the optimistic
            // baseline advance above leaves baseline == step, so isStepEqual stays
            // true and the dirty indicator never reappears after a failed rename.
            if (preSaveBaselineStep) {
              rollbackStepBaselineAt(set, { stepIndex, rollbackStep: preSaveBaselineStep });
            }
            throw err;
          }
        } else {
          // Explicit saves are deferred until the undo window expires,
          // mirroring the delete pattern. This gives the user time to
          // undo before the mutation fires. The pre-save baseline (captured
          // above) is threaded through so a failed save can roll back to it.
          const stepSmid = step.smid!;
          scheduleSave(stepSmid, gqlPayload, preSaveBaselineStep);

          if (preSaveBaselineStep) {
            const toastKey = `step-saved-${stepSmid}-${Date.now()}`;
            enqueueToast(CLINICAL_STEP_SAVED_TOAST.message, {
              ...getDefaultHPToastOptions({
                toastKey,
                autoHideDuration: HP_TOAST_UNDO_DURATION,
                onClose: () => {
                  flushPendingSaveForStep(stepSmid);
                  closeToast(toastKey);
                },
              }),
              ContentComponent: createStepToastContentComponent({
                actionLabel: CLINICAL_STEP_SAVED_TOAST.actionLabel,
                countdownDuration: HP_TOAST_UNDO_DURATION,
                onAction: () => {
                  cancelPendingSave(stepSmid);
                  rollbackStepBaselineAt(set, { stepIndex, rollbackStep: preSaveBaselineStep });
                },
              }),
            });
          }
        }
      },
    [
      updateStepMutation,
      enqueueToast,
      closeToast,
      trackOperation,
      currentCaseId,
      scheduleSave,
      cancelPendingSave,
      flushPendingSaveForStep,
      apolloClient,
    ]
  );

  /**
   * Re-orders the baseline array to match the post-reorder step order, keeping each step's own
   * last-saved content.
   *
   * Called after a drag-and-drop reorder. It used to be a blanket
   * `setBaseline(clone(currentSteps))`, which made baseline equal current for *every* step and so
   * silently cleared the unsaved-changes dot on any step that had pending edits (VX-4739). Steps are
   * matched by smid and only moved, never rewritten, so a dirty step stays dirty and a clean step
   * stays clean. The array is kept index-aligned with the steps because the revert paths index into
   * it (`currentBaseline[stepIndex]`).
   */
  const reorderBaselineToMatchSteps = useRecoilCallback(
    ({ snapshot, set }) =>
      async () => {
        const versioned = await snapshot.getPromise(steppedHPConfigurations);
        const currentSteps = getStepsFromVersioned(versioned);
        const currentBaseline = await snapshot.getPromise(clinicalStepsBaseline);
        if (!currentSteps || !currentBaseline) return;

        const baselineBySmid = new Map(
          currentBaseline.filter((step) => step.smid != null).map((step) => [step.smid, step])
        );
        applyBaselineOrder(set, {
          baseline: currentSteps.map((step, i) => {
            const matched = step.smid != null ? baselineBySmid.get(step.smid) : undefined;
            // A step with no baseline entry of its own (e.g. added but not yet persisted) keeps
            // whatever sat at its index, matching how the rest of this file treats such steps.
            return clone(matched ?? currentBaseline[i] ?? step);
          }),
        });
      },
    []
  );

  // Reorders run one at a time, chained here rather than fired as they arrive. Each one plans its
  // moves against the order its own drop started from, which is only what the server holds if every
  // earlier drop has already landed; two quick drags used to overlap, so the second planned against
  // positions the backend had not reached yet and moved a step to the wrong slot. Serializing also
  // means a rejected reorder has applied nothing, which is what lets the catch below put the step
  // bar back without having to ask the server what it actually kept.
  const reorderChainRef = useRef<Promise<void>>(Promise.resolve());
  // Claimed synchronously by each call so the tail of a superseded one can tell it no longer owns
  // the atom -- a later drop's optimistic order must not be overwritten by an earlier drop's.
  const reorderSeqRef = useRef(0);

  /**
   * Persist step order to the backend after a drag-and-drop reorder.
   *
   * Moves the steps that actually moved, one PATCH each, addressing them by smid (VX-4765). The
   * previous transport was a whole-HP PATCH carrying a `steps` array, which makes the backend
   * delete and recreate every step row: new smids for steps nobody touched, and every client-held
   * step reference dangling behind it (VX-4773). The step endpoint moves a row in place instead, so
   * the smids survive and a drag costs one write rather than a rewrite of the collection.
   *
   * `previousSmidOrder` is the order as it stood before the drop, which is what the server still
   * has. The moves are planned against it because the backend renumbers siblings around each move,
   * so a plan built from anything else would be addressing positions that no longer exist.
   *
   * Takes no HP id: every step is addressed by its own smid, and the endpoint resolves the parent
   * HP from the row. The caller still gates on there being an active HP, since the default HP has
   * no backend record to reorder.
   */
  const saveStepOrder = useRecoilCallback(
    ({ snapshot, set }) =>
      async (previousSmidOrder: ReadonlyArray<Nullable<string>>) => {
        // Both of these are claimed synchronously, while this is still the tail of the caller's
        // drop handler, so two drops in the same tick take their places in the chain in the order
        // the user made them rather than in whatever order their snapshot reads happen to resolve.
        const seq = ++reorderSeqRef.current;
        const previousReorder = reorderChainRef.current;

        const run = sendMoves();
        // The chain has to survive a rejected link, or one failed reorder strands every later one.
        // `sendMoves` handles its own errors, so this is belt and braces.
        reorderChainRef.current = run.catch(() => undefined);
        return run;

        async function sendMoves(): Promise<void> {
          // The snapshot is read up front, before waiting on the previous reorder. Recoil releases a
          // callback's snapshot once the callback settles, and the wait can outlast that; reading now
          // also pins the order this drop produced, which is what these PATCHes are meant to persist
          // even if a later drop moves the atom on again.
          const versioned = await snapshot.getPromise(steppedHPConfigurations);
          const currentSteps = versioned.steps;
          if (!currentSteps || currentSteps.length === 0) return;

          const moves = planStepOrderMoves(
            previousSmidOrder,
            currentSteps.map((step) => step.smid)
          );
          if (moves.length === 0) return;

          // Each PATCH carries the step's last-saved content alongside its new order. Sending the
          // step as it currently sits in the atom would commit any unsaved edits as a side effect of
          // a reorder, so a step the user never saved would come back from the server as saved
          // (VX-4739). Steps with no baseline entry (added during this session) fall back to their
          // current content, which is all the server has to go on for them.
          const orderBaseline = await snapshot.getPromise(clinicalStepsBaseline);
          const baselineBySmid = new Map(
            (orderBaseline ?? [])
              .filter((step) => step.smid != null)
              .map((step) => [step.smid, step] as const)
          );
          const stepsBySmid = new Map(
            currentSteps
              .filter((step) => step.smid != null)
              .map((step) => [step.smid, step] as const)
          );

          // Everything above is planning against state this drop already owns. The actual writes
          // wait for the previous reorder to finish, so the backend applies the drops in order and
          // each one's plan is against a list the server has already caught up to.
          await previousReorder;

          try {
            // Sequential, not parallel: each move's target index is expressed against the order the
            // previous move left behind, and the backend serializes these on a row lock anyway.
            for (const move of moves) {
              const step = stepsBySmid.get(move.smid);
              if (!step) continue;
              await trackOperation(
                updateStepMutation({
                  variables: {
                    stepId: move.smid,
                    payload: {
                      ...stepToGqlInput(baselineBySmid.get(move.smid) ?? step),
                      order: move.order,
                    },
                  },
                })
              );
            }
            // Re-assert the order in the viewer atom to bump its version. Nothing in this path
            // rewrites the step list any more, but a BroadcastChannel echo from viewer/1 carrying
            // pre-drop state can still land while the PATCHes are in flight, and the higher version
            // is what makes this window's order win that race.
            //
            // Skipped once a later drop has claimed the sequence: `currentSteps` is then a stale
            // order, and re-asserting it would roll the step bar back to the previous drop and
            // broadcast that as the newest state.
            if (seq === reorderSeqRef.current) {
              stageSteps(set, { update: () => currentSteps });
            }
            // Invalidate precache so page refresh fetches the new order
            await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA });

            // NOTE: broadcastHPInvalidation() is intentionally omitted here, for the same reason it is
            // omitted in `createStepOnBackend` above.
            //
            // The payloads above deliberately ship each step's *baseline* content so a reorder
            // doesn't commit unsaved edits. Broadcasting an invalidation made the other viewer window
            // refetch that now-clean server state, re-initialize `steppedHPConfigurations` from it, and
            // broadcast the higher-versioned result back here -- overwriting the dirty step with its
            // saved content. The step then genuinely equalled its baseline, so its unsaved-changes dot
            // disappeared: VX-4739 all over again, this time via the cross-window round trip.
            //
            // The other window does not need the broadcast to learn the new order:
            // `steppedHPConfigurations` is BroadcastChannel-synced and was re-asserted just above, and
            // the precache delete covers the next page load.
          } catch (error) {
            // Put the step bar back to the order the server still holds, so the user is not left
            // reading an order that only exists in this tab. Reorders are serialized above and
            // planned against `previousSmidOrder`, so a rejection here means the server never left
            // it -- there is no partially-applied order to reconcile against.
            //
            // Skipped when a later drop has already claimed the sequence. That drop owns the atom
            // now, its own plan started from this failed order, and its failure path will restore
            // the same thing; restoring here would just fight it.
            if (seq === reorderSeqRef.current) {
              stageSteps(set, {
                update: (prevSteps) => restoreStepOrder(prevSteps, previousSmidOrder) ?? prevSteps,
              });
            }
            enqueueToast(
              composeHPFailureMessage({
                action: 'save step order',
                fallback: CLINICAL_REORDER_FAILED_TOAST.message,
                error,
              }),
              {
                ...getDefaultHPToastOptions(),
                ContentComponent: createStepToastContentComponent({
                  icon: CLINICAL_REORDER_FAILED_TOAST.icon,
                }),
              }
            );
          }
        }
      },
    [updateStepMutation, enqueueToast, trackOperation]
  );

  const [isCreatingStep, setIsCreatingStep] = useRecoilState(isCreatingStepState);
  // Synchronous re-entry lock for createStepOnBackend. The `isCreatingStep` Recoil atom drives the
  // button's `disabled` attribute for UI/Playwright actionability, but the closure-captured value
  // inside the recoil callback lags a render behind the atom, so two synchronous clicks can both
  // slip past `if (isCreatingStep) return;` before React commits the new render. That manifests as
  // either duplicate `Step N` labels (both clicks proceed) or a silent no-op (second click catches
  // a stale closure that still reads `true`). A ref is updated imperatively and observed
  // synchronously, which makes the guard race-free regardless of render timing.
  const isCreatingStepLockRef = useRef(false);

  /**
   * Creates a new step on the backend immediately, cloning the current step,
   * and updates local Recoil state with the real backend smid.
   * Used in clinical mode so new steps always have a valid backend UUID.
   */
  const createStepOnBackend = useRecoilCallback(
    ({ snapshot, set }) =>
      async (hpSmid: string, sourceStepOverride?: StepConfiguration) => {
        if (isCreatingStepLockRef.current) return;
        isCreatingStepLockRef.current = true;
        setIsCreatingStep(true);

        // The lock + creating flag are always released in `finally`, so every
        // exit path (early return, mutation failure, accepted-but-no-smid)
        // leaves the button re-enabled and the re-entry guard cleared.
        try {
          const versioned = await snapshot.getPromise(steppedHPConfigurations);
          const currentSteps = versioned.steps;
          const currentIndex = await snapshot.getPromise(hangingProtocolStepIndex);
          if (!currentSteps) return;

          const sourceStep = sourceStepOverride ?? currentSteps[currentIndex];
          const newStepName = generateDefaultStepName(currentSteps);

          // Omit `smid` so `createDefaultStep` generates a client-side
          // `step-{uuid}` identifier. A null smid would block the rename handler
          // (`StepBar.tsx` guards on `step.smid != null`) and defeat smid-based
          // baseline tracking in `useClinicalStepDirtyState`.
          const newStep = createDefaultStep({
            name: newStepName,
            sourceStep,
            defaultLayout: '2,2',
          });

          // Optimistically add the step to both state and baseline so the UI
          // updates immediately without showing a dirty indicator. The temporary
          // client-generated smid will be replaced with the real backend smid
          // when the mutation resolves.
          const tempSmid = newStep.smid;
          addOptimisticStep(set, { step: newStep });
          // Identity stated rather than resolved: the append above is a lazily-applied `set` updater,
          // so `selectStep`'s lookup would read the pre-append list and stamp no smid at all.
          selectStepWithIdentity(set, { index: currentSteps.length, stepSmid: tempSmid });

          const gqlPayload = stepToGqlInput(newStep);

          // Tell useHangingProtocol not to re-initialize steps from the server until this resolves;
          // until then a fetched protocol cannot know about this step (VX-4811). Set immediately
          // above the try/catch so nothing between here and the mutation can throw past the matching
          // `endPendingStepCreate` calls and leave the guard stuck for the life of the window.
          beginPendingStepCreate(tempSmid);

          // Only the mutation itself should trigger the rollback + error toast.
          // Post-mutation side effects (cache update, broadcast, success toast)
          // must not propagate failures as "Failed to add step" — the server
          // already accepted the step.
          let result: Awaited<ReturnType<typeof createStepMutation>>;
          try {
            result = await trackOperation(
              createStepMutation({
                variables: { hpId: hpSmid, payload: gqlPayload },
              })
            );
          } catch (error) {
            endPendingStepCreate(tempSmid);
            // Roll back the optimistic add from both state and baseline
            rollbackOptimisticStep(set, { tempSmid: tempSmid! });
            selectStep(set, { index: currentIndex });
            enqueueToast(
              composeHPFailureMessage({
                action: 'create step',
                fallback: CREATE_FAILED_TOAST.message,
                error,
              }),
              {
                ...getDefaultHPToastOptions(),
                ContentComponent: createStepToastContentComponent({
                  icon: CREATE_FAILED_TOAST.icon,
                }),
              }
            );
            return;
          }

          const createdStep = result.data?.createHangingProtocolStep;
          const createdSmid = createdStep?.smid;
          // Cleared whether or not the server returned a usable smid: leaving it set would block
          // every later re-initialization for the life of the window.
          endPendingStepCreate(tempSmid);
          if (!createdSmid) return;

          // Replace the temporary smid with the real backend smid in both state and baseline.
          confirmOptimisticStep(set, { tempSmid: tempSmid!, serverSmid: createdSmid });

          // Move the viewport-override buckets from the temp smid to the real one in the same
          // batch. The create's network round-trip is a window in which a drop into the new step
          // would otherwise be filed under a key nothing will ever read again.
          if (currentCaseId && tempSmid != null) {
            renameStepBucket(set, { caseId: currentCaseId, from: tempSmid, to: createdSmid });
          }

          // A Save issued while this create was in flight could not be sent -- the step still carried
          // its client smid -- so run it now that the real one is in place. `saveStep` re-reads the
          // step from the atom and only schedules the deferred mutation, so this neither blocks the
          // create nor sends a payload frozen at request time. Deliberately not awaited: whoever
          // clicked Save is long gone, and awaiting it here would put a save's timing inside the
          // create's error handling. The step was appended, so its index is the pre-add length --
          // the same position `selectStepWithIdentity` was given above.
          if (tempSmid != null && takeSaveAwaitingCreate(tempSmid)) {
            void saveStep(currentSteps.length);
          }

          // Append the new step to the cached getBaseViewerData entries so
          // page reloads serve the up-to-date HP without a full network fetch.
          // Any failure here should NOT roll back the successful create.
          try {
            await updatePrecacheQuery({
              query: GET_BASE_VIEWER_DATA,
              transform: (data) => appendStepToMatchedHP(data, hpSmid, createdStep),
            });
          } catch (err) {
            logger.warn('Failed to update precache after create step', { err });
          }

          // NOTE: broadcastHPInvalidation() is intentionally omitted here.
          // steppedHPConfigurations already propagates the new step to viewer/1
          // via broadcastChannelSynchronizerEffect. Triggering a full HP refetch
          // on viewer/1 causes it to re-initialize steppedHPConfigurations with
          // stale server data (step not yet reflected), then broadcast a
          // higher-versioned result back to viewer/0 — overwriting the optimistic
          // add and producing duplicate/missing step names under rapid creation.

          enqueueToast(CLINICAL_STEP_CREATED_TOAST.message, {
            ...getDefaultHPToastOptions(),
            ContentComponent: createStepToastContentComponent({}),
          });
        } finally {
          isCreatingStepLockRef.current = false;
          setIsCreatingStep(false);
        }
      },
    [createStepMutation, currentCaseId, enqueueToast, saveStep, setIsCreatingStep, trackOperation]
  );

  /**
   * Fire the create mutation and swap the temp smid for the real one.
   *
   * Reads the current step from `steppedHPConfigurations` by `tempSmid` at
   * fire time so that any edits (rename, viewport changes) made during the
   * undo window are included in the payload. Using a closure-captured payload
   * here would send stale data to the backend.
   */
  const executeCreateOnBackend = useRecoilCallback(
    ({ snapshot, set }) =>
      async (tempSmid: string, hpId: string, preCreateIndex: number) => {
        deletePendingCreate(tempSmid);

        // Rebuild the payload from the current atom state, not a stale closure.
        const versioned = await snapshot.getPromise(steppedHPConfigurations);
        const currentStep = versioned.steps?.find((s) => s.smid === tempSmid);
        if (!currentStep) return;
        const gqlPayload = stepToGqlInput(currentStep);

        beginPendingStepCreate(tempSmid);

        // Only the mutation should trigger the rollback + error toast.
        // Post-mutation side effects (cache update, broadcast) must not
        // propagate failures as a create error when the server accepted.
        let result: Awaited<ReturnType<typeof createStepMutation>>;
        try {
          result = await trackOperation(
            createStepMutation({ variables: { hpId, payload: gqlPayload } })
          );
        } catch (error) {
          endPendingStepCreate(tempSmid);
          // Roll back
          rollbackOptimisticStep(set, { tempSmid });
          // The step index was optimistically advanced to the new step's position when it was
          // added. Restore it to the pre-create index so it does not point at the now-removed
          // step (matches createStepOnBackend's non-deferred rollback). VX-4441.
          selectStep(set, { index: preCreateIndex });
          enqueueToast(
            composeHPFailureMessage({
              action: 'create step',
              fallback: CREATE_FAILED_TOAST.message,
              error,
            }),
            {
              ...getDefaultHPToastOptions(),
              ContentComponent: createStepToastContentComponent({
                icon: CREATE_FAILED_TOAST.icon,
              }),
            }
          );
          return;
        }

        const createdStep = result.data?.createHangingProtocolStep;
        const createdSmid = createdStep?.smid;
        // Cleared whether or not the server returned a usable smid: leaving it set would block
        // every later re-initialization for the life of the window.
        endPendingStepCreate(tempSmid);
        if (!createdSmid) return;

        // Swap temp smid → real smid in the steps atom, preserving any subsequent local edits, and
        // baseline `currentStep` -- the value actually persisted -- so there is no false dirty dot.
        confirmOptimisticStep(set, {
          tempSmid,
          serverSmid: createdSmid,
          persistedStep: currentStep,
        });

        // Move the viewport-override buckets from the temp smid to the real one in the same batch,
        // or a drop made into the new step while its create was in flight is stranded under a key
        // nothing will ever read again.
        if (currentCaseId) {
          renameStepBucket(set, { caseId: currentCaseId, from: tempSmid, to: createdSmid });
        }

        // Same as the non-deferred path above: a Save requested while the step still carried its
        // client smid runs now. Redundant on the wire in this path -- `gqlPayload` was rebuilt at fire
        // time, so the server already has those edits -- but the save does more than PATCH: it folds
        // in and clears the step's viewport-override bucket, which is what keeps the edit from
        // resurfacing as untracked dirt on a later step navigation (VX-4617).
        if (takeSaveAwaitingCreate(tempSmid)) {
          const flushIndex = versioned.steps?.findIndex((s) => s.smid === tempSmid) ?? -1;
          if (flushIndex >= 0) void saveStep(flushIndex);
        }

        // Append the new step to the cached getBaseViewerData entries so
        // page reloads serve the up-to-date HP without evicting the cache.
        // Any failure here should NOT roll back the successful create.
        // Write `createdStep` as-is: `gqlPayload` above was rebuilt from
        // `currentStep` at fire time, so the server echo already reflects any
        // rename or viewport edit made during the undo window. Spreading
        // `currentStep` over it instead would overwrite the GQL array fields
        // (`layouts: [{window_id, layout}]`, `viewports: [{viewport_id, ...}]`)
        // with the frontend's keyed-record form, which Apollo rejects field by
        // field ("Missing field 'window_id' while writing result"). That wrote a
        // malformed HP into the precache and left the viewer blank on the next
        // reload. Matches the non-deferred path in `createStepOnBackend`.
        try {
          await updatePrecacheQuery({
            query: GET_BASE_VIEWER_DATA,
            transform: (data) =>
              appendStepToMatchedHP(data, hpId, { ...createdStep, smid: createdSmid }),
          });
        } catch (err) {
          logger.warn('Failed to update precache after deferred create step', { err });
        }

        // NOTE: broadcastHPInvalidation() is intentionally omitted here.
        // steppedHPConfigurations already propagates the new step to viewer/1 via
        // broadcastChannelSynchronizerEffect. Triggering a full HP refetch on viewer/1 causes it
        // to re-initialize steppedHPConfigurations with stale server data (step not yet
        // reflected), then broadcast a higher-versioned result back to viewer/0 — overwriting
        // the optimistic add and producing duplicate/missing step names under rapid creation.
      },
    [createStepMutation, currentCaseId, enqueueToast, saveStep, trackOperation]
  );

  /**
   * Add a new step when the current step has unsaved changes. Reverts the
   * current step, optimistically adds the new step, and shows an undo toast.
   * The actual create mutation fires when the toast dismisses. Undo restores
   * the dirty state and removes the new step.
   */
  const createStepWithRevert = useRecoilCallback(
    ({ snapshot, set }) =>
      (
        hpSmid: string,
        live?: { dirtyIndices: ReadonlySet<number>; baseline: StepConfiguration[] | null }
      ) => {
        const versioned = snapshot.getLoadable(steppedHPConfigurations).getValue();
        const currentSteps = versioned.steps;
        const currentIndex = snapshot.getLoadable(hangingProtocolStepIndex).getValue();
        if (!currentSteps) return;
        const steps: StepConfiguration[] = currentSteps;

        // Both the dirty set and the value reverted *to* come from the caller's live
        // `useClinicalStepDirtyState` when it has one, never from `snapshot`.
        //
        // `snapshot` is Recoil's `currentTree`, which lags a write Recoil has already accepted but
        // React has not committed yet. The previous action's Save advances the baseline through
        // `commitStepBaseline`, and the confirm click on "Add step with unsaved changes?" lands
        // after that, so the snapshot can still hold the baseline from one save earlier. Reverting
        // to it silently undid the save the user had just made: in
        // stepped-hp-priors-per-step.e2e.spec.ts the base step went back to its pre-widen 2,2
        // instead of the 2,3 it had been saved at, taking the prior hung in the new column with it.
        //
        // The dirty set alone was not enough (VX-4810, first attempt): sourcing it live but still
        // reverting to the snapshot's baseline reverts the right steps to the wrong value.
        // `useClinicalStepDirtyState` re-renders on every baseline write, so neither of its outputs
        // can lag that way. `StepBar` always passes both; the snapshot fallbacks below exist only
        // for unit tests that call this directly without mounting that hook.
        const currentBaseline =
          live?.baseline ?? snapshot.getLoadable(clinicalStepsBaseline).getValue();

        // Every dirty step is reverted, not just the active one. The confirmation modal that leads
        // here is gated on "any step has unsaved changes", so reverting only the active step left
        // the edits (and the yellow dot) in place whenever the dirty step was one the user had
        // already navigated away from (VX-4734).
        const dirtyIndices = live?.dirtyIndices
          ? [...live.dirtyIndices].filter((i) => i < steps.length)
          : steps.reduce<number[]>((acc, step, i) => {
              const baselineStep = currentBaseline?.[i];
              if (baselineStep != null && !isStepEqual(step, baselineStep)) acc.push(i);
              return acc;
            }, []);
        const currentStepIsDirty = dirtyIndices.includes(currentIndex);

        // Capture each dirty step's edits so UNDO can put them all back.
        const modifiedStepsByIndex = new Map<number, StepConfiguration>();
        for (const i of dirtyIndices) {
          if (steps[i]) modifiedStepsByIndex.set(i, clone(steps[i]));
        }

        if (dirtyIndices.length > 0 && currentBaseline) {
          revertStepsToBaseline(set, {
            indices: new Set(dirtyIndices),
            baseline: currentBaseline,
          });
        }

        // The reverted step always files against its own smid -- the source step's, since the new
        // step created below does not exist yet.
        const sourceBucket: StepBucketKey = steps[currentIndex]?.smid ?? FLAT_BUCKET;

        // The viewport/layout atoms describe what is on screen, so they only need restoring when
        // the step being displayed was one of the reverted ones.
        let modifiedStep: StepConfiguration | null = null;
        let modifiedViewports: DehydratedViewportsConfigurations | null = null;
        let modifiedLayouts: Layouts | null = null;
        if (currentStepIsDirty) {
          modifiedStep = modifiedStepsByIndex.get(currentIndex) ?? null;
          if (currentCaseId) {
            const vpState = snapshot
              .getLoadable(viewportsConfigurationsState(currentCaseId))
              .getValue();
            const bucketOverrides = readBucket(vpState.byStep, sourceBucket);
            modifiedViewports = bucketOverrides ? clone(bucketOverrides) : null;
            const allCaseLayouts = snapshot.getLoadable(layoutsForAllCasesState).getValue();
            modifiedLayouts = allCaseLayouts[currentCaseId]
              ? clone(allCaseLayouts[currentCaseId])
              : null;
          }

          // Same reasoning as revertStep's write-ahead: sourceBucket's key doesn't change here, and
          // dehydratedHangingProtocol's only other writer fires after paint.
          if (currentCaseId) {
            publishStepHangingProtocol(set, {
              caseId: currentCaseId,
              bucket: sourceBucket,
              dehydrated: stepViewportConfigsToDehydrated(currentBaseline![currentIndex].viewports),
            });
          }

          const baselineLayouts = stepLayoutsToLayouts(currentBaseline![currentIndex].layouts);
          // `modifiedLayouts` was captured above, so UNDO can still put the dirty layout back.
          applyProtocolLayouts(set, { caseId: currentCaseId, layouts: baselineLayouts });
          if (currentCaseId) {
            clearStepViewportOverrides(set, { caseId: currentCaseId, bucket: sourceBucket });
          }
        }

        // Build and optimistically add the new step (using modified data as source).
        // Omit `smid` so `createDefaultStep` generates a client-side
        // `step-{uuid}` identifier. See matching note in `createStepOnBackend`.
        const sourceStep = modifiedStep ?? steps[currentIndex];
        const newStepName = generateDefaultStepName(steps);
        const newStep = createDefaultStep({
          name: newStepName,
          sourceStep,
          defaultLayout: '2,2',
        });
        const tempSmid = newStep.smid!;

        addOptimisticStep(set, { step: newStep });
        // Same reason as `createStepOnBackend`: the append is lazy, so the identity is stated here.
        selectStepWithIdentity(set, { index: steps.length, stepSmid: tempSmid });

        // The active step is now the one just created, so what the viewer publishes has to describe
        // *it*.
        //
        // Add Step is a clone action: pressing it with an unsaved layout on screen should carry that
        // layout onto the new step, and `sourceStep` above does exactly that. But the revert block
        // published the SOURCE step's baseline -- correct while the source was still on screen -- and
        // nothing re-published when the index moved here. So the new step rendered on the source's
        // last-saved grid while its stored layouts held the cloned one, and the reader saw the change
        // they had just made silently disappear (VX-4768).
        //
        // applyProtocolLayouts rather than a bare publish: the step owns this layout, so the
        // case-scoped override must be cleared in the same breath or it outranks every later step.
        applyProtocolLayouts(set, {
          caseId: currentCaseId,
          layouts: stepLayoutsToLayouts(newStep.layouts),
        });
        if (currentCaseId) {
          // The new step, not the source, is what is on screen now -- files under its own (temp)
          // smid, which renameStepBucket moves to the real one once the create resolves.
          publishStepHangingProtocol(set, {
            caseId: currentCaseId,
            bucket: tempSmid,
            dehydrated: stepViewportConfigsToDehydrated(newStep.viewports),
          });
        }

        // Schedule deferred create. The payload is rebuilt at fire time inside
        // `executeCreateOnBackend` so that any rename or viewport edits made
        // during the undo window are included.
        setDeferredSaveCount((n) => n + 1);
        const toastKey = `step-create-revert-${tempSmid}-${Date.now()}`;
        const timer = setTimeout(() => {
          setDeferredSaveCount((n) => Math.max(0, n - 1));
          executeCreateOnBackend(tempSmid, hpSmid, currentIndex);
        }, HP_TOAST_UNDO_DURATION + 2_000);
        setPendingCreate(tempSmid, { timer, hpSmid, preCreateIndex: currentIndex });

        // Offer UNDO whenever something was actually discarded, which is now any dirty step rather
        // than only the active one (VX-4734).
        const toastDef =
          dirtyIndices.length > 0
            ? CLINICAL_STEP_CREATED_WITH_REVERT_TOAST
            : CLINICAL_STEP_CREATED_TOAST;
        enqueueToast(toastDef.message, {
          ...getDefaultHPToastOptions({
            toastKey,
            autoHideDuration: HP_TOAST_UNDO_DURATION,
            onClose: () => {
              // Flush: fire create immediately
              const pending = getPendingCreate(tempSmid);
              if (pending) {
                clearTimeout(pending.timer);
                deletePendingCreate(tempSmid);
                setDeferredSaveCount((n) => Math.max(0, n - 1));
                executeCreateOnBackend(tempSmid, hpSmid, currentIndex);
              }
              closeToast(toastKey);
            },
          }),
          ContentComponent: createStepToastContentComponent({
            actionLabel: toastDef.actionLabel,
            icon: toastDef.icon,
            countdownDuration: toastDef.actionLabel ? HP_TOAST_UNDO_DURATION : undefined,
            onAction: () => {
              // Cancel the deferred create and drop the optimistic step from both atoms. Shared with
              // Reset All so the two discard paths cannot disagree about what undoing a create means.
              discardPendingCreates(set, [tempSmid], (count) =>
                setDeferredSaveCount((n) => Math.max(0, n - count))
              );

              // Restore step index to the original position
              selectStep(set, { index: currentIndex });

              // Put back every step that was reverted, not just the active one, so UNDO is the
              // exact inverse of the discard (VX-4734).
              const otherModifiedSteps = Array.from(modifiedStepsByIndex.entries()).filter(
                ([index]) => index !== currentIndex
              );
              stageStepEdits(set, {
                stepsByIndex: new Map(otherModifiedSteps),
              });

              // Restore the active step's data, viewport overrides, and layouts
              restoreDirtyStepSnapshot(set, {
                stepIndex: currentIndex,
                currentCaseId,
                bucket: sourceBucket,
                modifiedStep,
                modifiedViewports,
                modifiedLayouts,
              });
            },
          }),
        });
      },
    [currentCaseId, enqueueToast, closeToast, executeCreateOnBackend]
  );

  /**
   * Pending backend deletions, keyed by step smid.
   * The timeout fires the actual DELETE after the undo window expires.
   * If undo is triggered, call cancelPendingDeletion to cancel the timeout.
   * Module-level so it survives StepBar remounts.
   */

  /** Fire the actual backend DELETE and update the baseline. */
  const executeDeleteOnBackend = useRecoilCallback(
    ({ set }) =>
      async (stepSmid: string) => {
        deletePendingDeletion(stepSmid);
        // The mutation is about to fire, so the server is about to agree with us either way.
        endPendingStepDelete(stepSmid);
        try {
          await trackOperation(deleteStepMutation({ variables: { stepId: stepSmid } }));
          dropBaselineAfterDelete(set, { smid: stepSmid });
          // Now the step is really gone, so its viewport overrides can go too. The prune effect in
          // `useHangingProtocol` deliberately spares a step whose deletion is still pending -- it is
          // restorable until this point, and pruning on the optimistic removal took its unsaved
          // overrides with it. That leaves this as the one place that knows the deletion committed,
          // and the prune effect does not re-run on its own here: nothing it depends on changes when a
          // deferred mutation resolves.
          if (currentCaseId != null) {
            clearStepViewportOverrides(set, { caseId: currentCaseId, bucket: stepSmid });
          }
          await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA });
        } catch (error) {
          enqueueToast(
            composeHPFailureMessage({
              action: 'delete step',
              fallback: DELETE_FAILED_TOAST.message,
              error,
            }),
            {
              ...getDefaultHPToastOptions(),
              ContentComponent: createStepToastContentComponent({
                icon: DELETE_FAILED_TOAST.icon,
              }),
            }
          );
        }
      },
    [currentCaseId, deleteStepMutation, enqueueToast, trackOperation]
  );

  /**
   * Schedule a backend DELETE after the undo toast window (30s).
   * Call this when a step is deleted locally. If the user clicks undo,
   * call cancelPendingDeletion to prevent the backend call.
   */
  const scheduleDeletion = useRecoilCallback(
    ({ set }) =>
      (stepSmid: string) => {
        if (isClientGeneratedSmid(stepSmid)) return;
        // Clear any existing timer for this smid (e.g. rapid delete/undo/delete)
        const existing = getPendingDeletion(stepSmid);
        if (existing) clearTimeout(existing);

        // Immediately sync baseline so dirty indicators don't appear on remaining steps while
        // waiting for the backend delete. The step's own entry is stashed so an undo can restore
        // the exact pre-delete dirty state (VX-4625).
        dropBaselineForPendingDelete(set, { smid: stepSmid });

        const timer = setTimeout(() => {
          executeDeleteOnBackend(stepSmid);
        }, HP_TOAST_UNDO_DURATION + 2_000); // Slightly longer than the toast auto-hide
        setPendingDeletion(stepSmid, timer);
        // Tell every window the step is on its way out, so a peer refetching during the undo window
        // reads "still on the server" as a deletion in progress rather than as its own staleness.
        beginPendingStepDelete(stepSmid);
      },
    [executeDeleteOnBackend]
  );

  /**
   * Cancel a pending backend deletion (called on undo) and restore the
   * step's pre-delete baseline entry, so its dirty state reflects whatever
   * it was before the delete rather than always coming back clean (VX-4625).
   *
   * By the time this runs, the caller (StepBar's handleUndoDelete) has already restored the
   * full pre-delete step array into steppedHPConfigurations, with the undone step back at its
   * original index. revertStep/revertAllStepsToBaseline read baseline[stepIndex] positionally
   * against that array, so the restored baseline entry must be spliced back into the same index
   * rather than appended -- appending would misalign every step after the undone one whenever
   * the deleted step wasn't already last.
   *
   * The index comes from the stash `scheduleDeletion` captured, not from a lookup here. Looking it
   * up was the VX-4815 bug: `handleUndoDelete` restores the steps atom and then calls this
   * synchronously, so this callback's snapshot still predates that write, the `findIndex` returned
   * -1, and the entry was appended.
   */
  const cancelPendingDeletion = useRecoilCallback(
    ({ set }) =>
      (stepSmid: string) => {
        const timer = getPendingDeletion(stepSmid);
        if (timer) clearTimeout(timer);
        endPendingStepDelete(stepSmid);
        restoreBaselineForUndoneDelete(set, { smid: stepSmid });
        deletePendingDeletion(stepSmid);
      },
    []
  );

  /** Immediately execute a single pending deletion. */
  const flushPendingDeletionForStep = useCallback(
    (stepSmid: string) => {
      const timer = getPendingDeletion(stepSmid);
      if (timer) {
        clearTimeout(timer);
        executeDeleteOnBackend(stepSmid);
      }
    },
    [executeDeleteOnBackend]
  );

  /**
   * Immediately execute all pending backend deletions, clearing their timers. Resolves once every
   * flushed deletion has settled, for the same ordering reason as `flushPendingSaves`.
   */
  const flushPendingDeletions = useCallback(async (): Promise<void> => {
    const settled = pendingDeletionEntries().map(([stepSmid, timer]) => {
      clearTimeout(timer);
      return executeDeleteOnBackend(stepSmid);
    });
    clearPendingDeletions();
    await Promise.all(settled);
  }, [executeDeleteOnBackend]);

  /** Immediately fire all pending deferred creates, clearing their timers. */
  const flushPendingCreate = useCallback(() => {
    const entries = pendingCreateEntries();
    if (entries.length === 0) return;
    for (const [tempSmid, { timer, hpSmid, preCreateIndex }] of entries) {
      clearTimeout(timer);
      setDeferredSaveCount((n) => Math.max(0, n - 1));
      executeCreateOnBackend(tempSmid, hpSmid, preCreateIndex);
    }
    clearPendingCreates();
  }, [executeCreateOnBackend]);

  // If the page navigates away during an undo window, fire any deferred backend
  // writes immediately (best-effort) so the user's pending save/create/delete is
  // not silently dropped when the deferred timer never gets to run.
  useOnUnload(() => {
    // Nothing to await on unload -- the page is going away, so these are dispatched best-effort.
    void flushPendingSaves();
    void flushPendingDeletions();
    flushPendingCreate();
  });

  return {
    revertStep,
    saveStep,
    saveStepOrder,
    reorderBaselineToMatchSteps,
    revertAllStepsToBaseline,
    createStepOnBackend,
    createStepWithRevert,
    isCreatingStep,
    scheduleDeletion,
    cancelPendingDeletion,
    flushPendingDeletionForStep,
    flushPendingDeletions,
    flushPendingSaves,
    pendingOperations,
    deferredSaveCount,
  };
}
